import { type FirebaseOptions, initializeApp } from 'firebase/app';
import { connectStorageEmulator, getStorage } from 'firebase/storage';
import i18n from 'i18next';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { reportError } from 'src/modules/logs/Sentry';

export const firebaseConfig = JSON.parse(
  import.meta.env.VITE_FIREBASE_CONFIG as string,
) as FirebaseOptions;

initializeApp(firebaseConfig);

export type AuthError = {
  code?: string;
  message: string;
};

export const authErrorCodeToMessage = ({
  code,
  message,
}: AuthError): string | undefined => {
  if (
    message === 'The user canceled the sign-in flow.' ||
    message ===
      'L’opération n’a pas pu s’achever. (com.apple.AuthenticationServices.AuthorizationError erreur 1001.)'
  ) {
    code = 'user-cancelled';
  }
  switch (code) {
    case 'auth/email-already-in-use':
    case 'email-already-in-use':
      return i18n.t('login.errors.emailAlreadyInUse');
    case 'auth/user-not-found':
    case 'user-not-found':
      return i18n.t('login.errors.userNotFound');
    case 'auth/wrong-password':
    case 'wrong-password':
    case 'auth/invalid-login-credentials':
    case 'invalid-login-credentials':
      return i18n.t('login.errors.wrongPassword');
    case 'auth/invalid-action-code':
    case 'invalid-action-code':
      return i18n.t('login.errors.linkExpired');
    case 'auth/redirect-cancelled-by-user':
    case 'redirect-cancelled-by-user':
    case 'auth/user-cancelled':
    case 'user-cancelled':
      return undefined;
    default:
      return i18n.t('login.errors.defaultError');
  }
};

const syncAuthLanguage = async (languageCode?: string) => {
  if (!languageCode) return;
  try {
    await FirebaseAuthentication.setLanguageCode({ languageCode });
  } catch (error) {
    reportError('Failed to sync the auth language', error);
  }
};

void syncAuthLanguage(i18n.language ?? 'fr');
i18n.on('languageChanged', language => {
  void syncAuthLanguage(language);
});

export const getCurrentUser = async () => {
  const result = await FirebaseAuthentication.getCurrentUser();
  return result.user;
};

export const getCurrentUserIdToken = async () => {
  const currentUser = await getCurrentUser();
  if (!currentUser) {
    return;
  }
  return (await FirebaseAuthentication.getIdToken()).token;
};

// In-memory copy of the ID token for the app-session stamp, which must fire in the same
// tick as a lifecycle event: awaiting the native bridge there loses the beacon when the
// WebView is suspended. Refreshed on session open and on every heartbeat tick.
let cachedIdToken: string | undefined;

export const getCachedIdToken = () => cachedIdToken;

// The SDK only refreshes a token in its last 30 s, but the cached copy is used minutes later.
export const ID_TOKEN_MIN_REMAINING_MS = 10 * 60 * 1000;

export const refreshIdTokenCache = async () => {
  const currentUser = await getCurrentUser();
  if (!currentUser) {
    cachedIdToken = undefined;
    return cachedIdToken;
  }
  const { expirationTime } = await FirebaseAuthentication.getIdTokenResult();
  const forceRefresh = expirationTime - Date.now() < ID_TOKEN_MIN_REMAINING_MS;
  cachedIdToken = (await FirebaseAuthentication.getIdToken({ forceRefresh })).token;
  return cachedIdToken;
};

if (import.meta.env.MODE === 'development') {
  // eslint-disable-next-line react-hooks/rules-of-hooks -- not a React hook
  await FirebaseAuthentication.useEmulator({
    host: import.meta.env.VITE_FIREBASE_EMULATOR_HOST,
    port: import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_PORT || 9099,
    scheme: 'http',
  });
  const firebaseStorage = getStorage();
  connectStorageEmulator(
    firebaseStorage,
    import.meta.env.VITE_FIREBASE_EMULATOR_HOST as string,
    import.meta.env.VITE_FIREBASE_STORAGE_EMULATOR_PORT || 9199,
  );
}
